/**
 * Workspace Members Slice - Zustand Store
 *
 * Caches the workspace member suggestion list (used by member multi-selects,
 * assignee pickers, and filter pills) in shared state so that multiple
 * consumers no longer each fire their own `GET /workspaces/{id}/members`
 * request. Previously `useWorkspaceMemberSuggestions` fetched per-instance via
 * useState/useEffect, which meant the filter control and the filter pills
 * double-fetched the same list.
 *
 * Fetch semantics are stale-while-revalidate: every call refreshes the list,
 * and only concurrent in-flight requests for the same workspace are deduped
 * (via `isLoading`). A completed load no longer blocks a fresh refetch, so each
 * new mount/trigger re-fetches the members. On a workspace switch the previous
 * list is cleared so the UI never shows the wrong workspace's members.
 *
 * The in-flight request's AbortController is kept at module scope — NOT in the
 * Immer state — because Immer freezes drafted objects.
 */

import { apiClient } from '@/services/api';
import { API_ENDPOINTS } from '@/constants/api';
import { getMemberLabel } from '@/utils/getMemberLabel';
import type { WorkspaceMember } from '@/types/workspace';
import type { GetWorkspaceMembersResponse } from '@/types/api/responses';

// Member suggestion lists are small; a single large page avoids pagination
// round-trips for the dropdowns/pills that consume this slice.
const MEMBER_SUGGESTIONS_PAGE_SIZE = 200;
const MEMBER_SUGGESTIONS_PAGE = 1;

// Module-level (not Immer state): Immer freezes drafted objects, so the live
// AbortController cannot live in the store. Tracks the latest in-flight fetch.
let memberSuggestionsAbortController: AbortController | null = null;

// ===== SLICE INTERFACE =====

export interface WorkspaceMembersSlice {
  workspaceMembers: {
    members: WorkspaceMember[];
    isLoading: boolean;
    /** Workspace of the current/in-flight members (concurrent-dedup key). */
    loadedWorkspaceId: string | null;

    /**
     * Fetch member suggestions for a workspace (stale-while-revalidate).
     * Same-workspace concurrent calls dedup via `isLoading`; each fresh trigger
     * refetches the list.
     */
    fetchMemberSuggestions: (workspaceId: string) => Promise<void>;
  };
}

// ===== SLICE CREATOR =====

export const createWorkspaceMembersSlice = (
  set: (fn: (state: WorkspaceMembersSlice) => void) => void,
  get: () => WorkspaceMembersSlice,
  _store: unknown
) => ({
  workspaceMembers: {
    members: [] as WorkspaceMember[],
    isLoading: false,
    loadedWorkspaceId: null as string | null,

    fetchMemberSuggestions: async (workspaceId: string) => {
      const current = get().workspaceMembers;

      // Dedup only concurrent in-flight calls for the same workspace (the filter
      // control and the filter pills mount together). Zustand `set` is synchronous,
      // so the first caller flips isLoading=true before its await and the second
      // caller in the same render no-ops here. A completed load no longer blocks a
      // fresh refetch — each new mount/trigger refreshes the list.
      if (current.loadedWorkspaceId === workspaceId && current.isLoading) {
        return;
      }

      // Abort any previous in-flight request (e.g. rapid workspace switch).
      memberSuggestionsAbortController?.abort();
      const controller = new AbortController();
      memberSuggestionsAbortController = controller;

      // Mark loading and claim the workspace immediately so parallel callers
      // in the same render are deduped by the guard above.
      const isWorkspaceSwitch = current.loadedWorkspaceId !== workspaceId;
      set((state) => {
        state.workspaceMembers.isLoading = true;
        state.workspaceMembers.loadedWorkspaceId = workspaceId;
        // On a workspace switch, drop the previous workspace's members so the UI
        // never shows the wrong list while the new fetch is in flight. On a
        // same-workspace refresh keep the current list (no flicker).
        if (isWorkspaceSwitch) {
          state.workspaceMembers.members = [];
        }
      });

      const params = new URLSearchParams({
        page: MEMBER_SUGGESTIONS_PAGE.toString(),
        pageSize: MEMBER_SUGGESTIONS_PAGE_SIZE.toString(),
      });

      try {
        const response = await apiClient.get<GetWorkspaceMembersResponse>(
          `${API_ENDPOINTS.WORKSPACES.MEMBERS(workspaceId)}?${params}`,
          { signal: controller.signal }
        );

        // A newer fetch (e.g. a rapid workspace switch) aborted this controller
        // after the response had already resolved — drop the stale result so it
        // can't clobber the newer workspace's state. The aborter owns isLoading,
        // so just return.
        if (controller.signal.aborted) {
          return;
        }

        const sorted = [...response.data.items].sort((memberA, memberB) =>
          getMemberLabel(memberA)
            .toLowerCase()
            .localeCompare(getMemberLabel(memberB).toLowerCase())
        );

        set((state) => {
          state.workspaceMembers.members = sorted;
          state.workspaceMembers.isLoading = false;
        });
      } catch (error) {
        // Aborted request (workspace switch / unmount): the axios response
        // interceptor has already transformed the CanceledError into a plain
        // ApiError (stripping the `name`), so check the controller's own signal
        // instead of `error.name === 'AbortError'`.
        if (controller.signal.aborted) {
          return;
        }
        console.error('Failed to fetch member suggestions:', error);
        set((state) => {
          state.workspaceMembers.isLoading = false;
          // Reset the dedup key so a later mount can retry after a failure.
          state.workspaceMembers.loadedWorkspaceId = null;
        });
      }
    },
  },
});
